import type { ResetPasswordTokenValidity, User } from "api/api.typing";
import api from "./api";

export const getXSRFToken = () => {
  return api.get("/api");
};

export const fetchMe = async (): Promise<User> => {
  const response = await api.get<User>("/api/users/me", {
    validateStatus: (status) => status === 200 || status === 401,
  });
  return response.data;
};

export type TwoFactorChallengeBody = {
  pendingToken: string;
  maskedEmail: string;
};

export type LoginPasswordResult = { outcome: "session" } | { outcome: "two-factor"; challenge: TwoFactorChallengeBody };

export const requestLogin = async (
  email: string,
  password: string,
  rememberMe: boolean
): Promise<LoginPasswordResult> => {
  const response = await api.post<TwoFactorChallengeBody>("/api/account/login", {
    username: email,
    password,
    rememberMe,
  });
  if (response.status === 200) {
    return { outcome: "session" };
  }
  if (response.status === 202) {
    const data = response.data;
    return {
      outcome: "two-factor",
      challenge: {
        pendingToken: data.pendingToken,
        maskedEmail: data.maskedEmail,
      },
    };
  }
  throw new Error(`Unexpected login status: ${response.status}`);
};

export const requestVerifyTwoFactor = async (
  pendingToken: string,
  code: string,
  rememberDevice: boolean
): Promise<void> => {
  await api.post("/api/account/login/twofactor/verify", {
    pendingToken,
    code,
    rememberDevice,
  });
};

export const requestLogout = async (): Promise<void> => {
  return api.post("/api/account/logoff");
};

export const requestResetPasswordLink = async (email: string): Promise<string> => {
  await api.post("/api/account/forgotpassword", { email });
  return email;
};

export const requestUpdatePassword = async (newPassword: string, resetPasswordToken: string): Promise<void> => {
  await api.post("/api/account/resetpassword", {
    newPassword,
    resetPasswordToken,
  });
};

export const getResetPasswordTokenValidity = async (token: string | null): Promise<ResetPasswordTokenValidity> => {
  if (!token) {
    return { isValid: false };
  }
  const response = await api.get<{ isValid: boolean }>(`/api/account/checkResetPasswordToken?token=${token}`);
  return { isValid: response.data.isValid };
};
